import { useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils/formatters';
import type { PromiseToPay, PromisedTransaction } from '@/types/payments';

interface PromiseAlertBannerProps {
  promise: PromiseToPay;
  promisedTransactions: PromisedTransaction[];
  onUsePromise: () => void;
  onIgnore: () => void;
}

export function PromiseAlertBanner({
  promise,
  promisedTransactions,
  onUsePromise,
  onIgnore,
}: PromiseAlertBannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  if (isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsDismissed(true);
    onIgnore();
  };

  return (
    <Alert className="relative border-blue-200 bg-blue-50">
      <button
        onClick={handleDismiss}
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">Dismiss</span>
      </button>

      <AlertTitle className="text-blue-900 flex items-center gap-2 mb-2">
        <span className="text-xl">🎯</span>
        <span className="font-semibold">Promise to Pay Detected</span>
      </AlertTitle>

      <AlertDescription className="text-blue-800 space-y-3">
        <p>
          This customer promised to pay{' '}
          <strong>{formatCurrency(promise.promisedAmount)}</strong> on{' '}
          <strong>{formatDate(promise.promisedDate.toISOString())}</strong>
        </p>

        {promisedTransactions.length > 0 && (
          <div>
            <div className="font-medium mb-2">Promised Transactions:</div>
            <ul className="space-y-1 ml-4">
              {promisedTransactions
                .sort((a, b) => a.priority - b.priority)
                .map((tx) => (
                  <li key={tx.transactionId} className="text-sm">
                    • {tx.transactionNumber}:{' '}
                    {formatCurrency(tx.promisedAmount)}{' '}
                    <span className="text-blue-600">(Priority {tx.priority})</span>
                  </li>
                ))}
            </ul>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button
            onClick={onUsePromise}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            Use Promise Allocation
          </Button>
          <Button
            onClick={handleDismiss}
            size="sm"
            variant="outline"
            className="border-blue-300 text-blue-700 hover:bg-blue-100"
          >
            Ignore Promise
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
